/**
 * Eligibility verification router (US-031.4).
 *
 * The compliance/admin/owner Members of a university Organization see a
 * queue of their athletes who have entered an NCAA ID but haven't been
 * verified yet. They can:
 *   - listPending — read the queue, scoped to orgs the caller can verify in
 *   - verify     — set the three eligibility columns + audit log
 *   - reject     — notify the athlete with a reason + audit log
 *
 * The schema columns (`eligibilityVerifiedAt`, `eligibilityVerifiedBy`,
 * `eligibilityVerificationSource`) already shipped with US-031.1; this
 * router is the first writer of those columns.
 *
 * Authorization model:
 *   - Caller must be a Member of at least one Organization with role in
 *     ('owner', 'admin', 'compliance').
 *   - Target athlete must be a Member of the same Organization. Without
 *     that, a compliance officer at school A could verify an athlete at
 *     school B.
 *
 * @package @app/api
 */

import { ORPCError } from '@orpc/server';
import { z } from 'zod';
import { writeAuditLog } from '../lib/audit';
import { protectedProcedure } from '../middleware/auth';

const COMPLIANCE_ROLES = ['owner', 'admin', 'compliance'] as const;

/**
 * Return the set of orgIds the caller has compliance-style standing in,
 * along with each org's name (for downstream display). Empty when the
 * caller has no qualifying memberships.
 */
async function callerComplianceOrgs(
  // biome-ignore lint/suspicious/noExplicitAny: prisma client type
  db: any,
  userId: string,
): Promise<Array<{ id: string; name: string }>> {
  const members = await db.member.findMany({
    where: { userId, role: { in: COMPLIANCE_ROLES as unknown as string[] } },
    select: { organization: { select: { id: true, name: true } } },
  });
  return members.map((m: { organization: { id: string; name: string } }) => m.organization);
}

/**
 * Verify that `targetUserId` is a member of one of the caller's compliance
 * orgs. Returns the matching org row when authorized, throws otherwise.
 */
async function requireSharedOrg(
  // biome-ignore lint/suspicious/noExplicitAny: prisma client type
  db: any,
  callerId: string,
  targetUserId: string,
): Promise<{ id: string; name: string }> {
  const callerOrgs = await callerComplianceOrgs(db, callerId);
  if (callerOrgs.length === 0) {
    throw new ORPCError('FORBIDDEN', { message: 'You are not a compliance member of any org.' });
  }
  const orgIds = callerOrgs.map((o) => o.id);
  const targetMembership = await db.member.findFirst({
    where: { userId: targetUserId, organizationId: { in: orgIds } },
    select: { organizationId: true },
  });
  if (!targetMembership) {
    throw new ORPCError('FORBIDDEN', {
      message: 'Athlete is not a member of any org you can verify.',
    });
  }
  // biome-ignore lint/style/noNonNullAssertion: matched above
  return callerOrgs.find((o) => o.id === targetMembership.organizationId)!;
}

export const eligibilityRouter = {
  /**
   * Athletes who have entered an NCAA ID but haven't been verified yet,
   * scoped to the caller's compliance orgs. Returns the minimum the queue
   * UI needs to render a row + the action buttons.
   */
  listPending: protectedProcedure.handler(async ({ context }) => {
    const { db, session } = context;
    const orgs = await callerComplianceOrgs(db, session.user.id);
    if (orgs.length === 0) return { canVerify: false, orgs: [], athletes: [] };

    const orgIds = orgs.map((o) => o.id);
    const memberships = await db.member.findMany({
      where: {
        organizationId: { in: orgIds },
        user: {
          ncaaId: { not: null },
          eligibilityVerifiedAt: null,
        },
      },
      select: {
        organization: { select: { id: true, name: true } },
        user: {
          select: {
            id: true,
            name: true,
            email: true,
            image: true,
            sport: true,
            classYear: true,
            ncaaId: true,
            ncaaIdAddedAt: true,
          },
        },
      },
      orderBy: { createdAt: 'desc' },
    });

    return {
      canVerify: true,
      orgs,
      athletes: memberships.map(
        // biome-ignore lint/suspicious/noExplicitAny: prisma row shape
        (m: any) => ({
          id: m.user.id,
          name: m.user.name,
          emailMasked: maskEmail(m.user.email),
          image: m.user.image,
          sport: m.user.sport,
          classYear: m.user.classYear,
          ncaaId: m.user.ncaaId,
          ncaaIdAddedAt: m.user.ncaaIdAddedAt?.toISOString() ?? null,
          organizationId: m.organization.id,
          organizationName: m.organization.name,
        }),
      ),
    };
  }),

  /**
   * Mark an athlete verified. Writes the three eligibility columns and a
   * `compliance / eligibility.verified` audit row.
   *
   * Uses the raw (un-enhanced) prisma client for the User write because
   * the User model's ZenStack policy only allows `update` when
   * `auth() == this` — a compliance officer writing another athlete's
   * eligibility row is denied. The handler's own `requireSharedOrg`
   * check is the actual authorization boundary; the row-level policy is
   * for self-serve writes only. Same pattern as `organization.router.ts`
   * (commit afed847).
   */
  verify: protectedProcedure
    .input(z.object({ userId: z.string() }))
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const callerId = session.user.id;
      const org = await requireSharedOrg(db, callerId, input.userId);

      const target = await db.user.findUnique({
        where: { id: input.userId },
        select: { id: true, name: true, email: true, ncaaId: true, eligibilityVerifiedAt: true },
      });
      if (!target) throw new ORPCError('NOT_FOUND', { message: 'Athlete not found' });
      if (!target.ncaaId) {
        throw new ORPCError('BAD_REQUEST', {
          message: "Athlete hasn't entered an NCAA ID yet.",
        });
      }
      if (target.eligibilityVerifiedAt) {
        // Idempotent — return the existing state instead of clobbering it.
        return { ok: true, status: 'already_verified' as const };
      }

      const { prisma: rawPrisma } = await import('@app/db');
      const now = new Date();
      await rawPrisma.user.update({
        where: { id: input.userId },
        data: {
          eligibilityVerifiedAt: now,
          eligibilityVerifiedBy: callerId,
          eligibilityVerificationSource: 'school_attestation',
        },
      });

      await writeAuditLog({
        action: 'eligibility.verified',
        category: 'compliance',
        actor: {
          id: callerId,
          name: session.user.name,
          email: session.user.email,
          role: session.user.role ?? null,
        },
        target: { type: 'user', id: input.userId, name: target.name },
        context: {
          organizationId: org.id,
          organizationName: org.name,
          ncaaId: target.ncaaId,
          source: 'school_attestation',
        },
      });

      return { ok: true, status: 'verified' as const, verifiedAt: now.toISOString() };
    }),

  /**
   * Reject a pending verification. Does NOT clear the athlete's NCAA ID —
   * the athlete still owns the value; we just nudge them to fix it via an
   * in-app notification carrying the compliance officer's reason. Writes a
   * `compliance / eligibility.rejected` audit row with `success=false`
   * and the reason in `errorMessage`.
   */
  reject: protectedProcedure
    .input(
      z.object({
        userId: z.string(),
        reason: z.string().min(1, 'Add a reason').max(500),
      }),
    )
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const callerId = session.user.id;
      const org = await requireSharedOrg(db, callerId, input.userId);

      const target = await db.user.findUnique({
        where: { id: input.userId },
        select: { id: true, name: true, email: true, ncaaId: true, eligibilityVerifiedAt: true },
      });
      if (!target) throw new ORPCError('NOT_FOUND', { message: 'Athlete not found' });
      if (target.eligibilityVerifiedAt) {
        throw new ORPCError('BAD_REQUEST', {
          message: 'Cannot reject a verification that has already been approved.',
        });
      }

      // Raw prisma — Notification model's ZenStack policy allows
      // `read,update` to self and `all` to admin only; no create path
      // for a compliance officer notifying another user. Same justification
      // as the `verify` mutation above.
      const { prisma: rawPrisma, createId } = await import('@app/db');
      const title = `${org.name} couldn't verify your NCAA ID`;
      const body = `Reason from compliance: ${input.reason.trim()}\n\nNext step: open your profile, double-check the NCAA Eligibility ID, and save the corrected value. Your school will see it in their verification queue again.`;
      await rawPrisma.notification.create({
        data: {
          id: createId(),
          userId: input.userId,
          type: 'system',
          title,
          body,
          actionUrl: '/profile',
        },
      });

      // Email the athlete so they don't wait until next sign-in to find
      // out their submission was rejected. Best-effort: an email failure
      // doesn't undo the in-app row or block the audit log.
      // Routes through @app/notifications (already a dep) instead of
      // @app/email directly — see compliance.recordDecision for the
      // build-break rationale.
      if (target.email) {
        try {
          const { sendNotification } = await import('@app/notifications');
          const appUrl = process.env.BETTER_AUTH_URL ?? process.env.VITE_APP_URL ?? 'https://www.nilboard.app';
          const linkUrl = `${appUrl.replace(/\/$/, '')}/profile`;
          await sendNotification({
            userId: input.userId,
            title,
            body: `${body}\n\nOpen your profile: ${linkUrl}`,
            channels: ['email'],
            email: target.email,
          });
        } catch (e) {
          console.error('[eligibility.reject] email send failed:', e);
        }
      }

      await writeAuditLog({
        action: 'eligibility.rejected',
        category: 'compliance',
        actor: {
          id: callerId,
          name: session.user.name,
          email: session.user.email,
          role: session.user.role ?? null,
        },
        target: { type: 'user', id: input.userId, name: target.name },
        context: {
          organizationId: org.id,
          organizationName: org.name,
          ncaaId: target.ncaaId,
        },
        success: false,
        errorMessage: input.reason.trim(),
      });

      return { ok: true, status: 'rejected' as const };
    }),
};

function maskEmail(email: string | null | undefined): string {
  if (!email) return '';
  const [u, d] = email.split('@');
  return `${u.slice(0, 2)}***@${d ?? ''}`;
}
